import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { queryKeys, useApiMutation, useApiQuery } from '../../api/hooks';
import type { RefundDecisionRequest, RefundResponse } from '../../api/types';
import { AsyncSection } from '../../components/AsyncSection';
import { PageHeader } from '../../components/PageHeader';
import { PanelHeading } from './PanelHeading';
import { ErrorState } from '../../components/states/StateViews';
import { useAuthStore } from '../../auth/authStore';
import { useLocalisedName } from '../../i18n/localisedName';
import { useBranchPicker } from '../scheduling/useBranchPicker';
import { money } from '../catalog/money';

/**
 * The refund queue (FR-904, RULE-014, DEC-039).
 *
 * Two people, always: whoever asked is shown next to the decision, and the server refuses an
 * approval from the person who raised it. The screen shows that rather than explaining it — the
 * requester's name is the first column, so an approver always knows whose request they are
 * agreeing to.
 */
export function RefundsPage() {
  const { t } = useTranslation();
  const { name } = useLocalisedName();
  const { clinicId, branchId, branches, choose } = useBranchPicker();
  const permissions = useAuthStore((state) => state.permissions);
  const canDecide = permissions.has('REFUND_APPROVE');

  const [note, setNote] = useState<Record<string, string>>({});

  const refunds = useApiQuery<RefundResponse[]>(
    queryKeys.refunds(clinicId ?? '', branchId ?? ''),
    `/clinics/${clinicId}/refunds${branchId ? `?branchId=${branchId}` : ''}`,
    { enabled: Boolean(clinicId) },
  );

  const decide = useApiMutation<RefundResponse, { readonly id: string; readonly outcome: 'approve' | 'decline' }>(
    ({ id, outcome }) => ({
      path: `/clinics/${clinicId}/refunds/${id}/${outcome}`,
      options: { method: 'POST', body: { note: note[id] } satisfies RefundDecisionRequest },
    }),
    { invalidates: [queryKeys.refunds(clinicId ?? '', branchId ?? '')] },
  );

  if (!clinicId) {
    return <PageHeader title={t('nav.items.refunds')} description={t('home.noClinic')} />;
  }

  return (
    <>
      <PageHeader title={t('nav.items.refunds')} description={t('refunds.description')} />

      <div className="filters">
        <label className="field">
          <span>{t('board.branch')}</span>
          <select value={branchId ?? ''} onChange={(event) => choose(event.target.value)}>
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {name(branch, branch.code)}
              </option>
            ))}
          </select>
        </label>
      </div>

      {decide.isError ? <ErrorState error={decide.error} /> : null}

      <AsyncSection query={refunds}>
        {(rows) => (
          <section className="panel">
            <PanelHeading title={t('refunds.queue')} hint={t('refunds.queueHint')} />
            {rows.length === 0 ? (
              <p className="muted">{t('refunds.none')}</p>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>{t('sales.receipt')}</th>
                      <th>{t('checkout.patient')}</th>
                      <th>{t('refunds.amount')}</th>
                      <th>{t('checkout.method')}</th>
                      <th>{t('refunds.reason')}</th>
                      <th>{t('refunds.requestedBy')}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((refund) => (
                      <tr key={refund.id}>
                        <td>{refund.receiptNumber}</td>
                        <td>{refund.patientName}</td>
                        <td>{money(refund.amount)}</td>
                        <td>{t(`checkout.methods.${refund.method}`)}</td>
                        <td>{refund.reason}</td>
                        <td>{refund.requestedByName}</td>
                        <td className="actions">
                          {canDecide && refund.status === 'REQUESTED' ? (
                            <>
                              <input
                                aria-label={t('refunds.note')}
                                placeholder={t('refunds.note')}
                                value={note[refund.id ?? ''] ?? ''}
                                onChange={(event) =>
                                  setNote((current) => ({ ...current, [refund.id ?? '']: event.target.value }))
                                }
                              />
                              <button
                                type="button"
                                className="primary"
                                disabled={decide.isPending}
                                onClick={() => decide.mutate({ id: refund.id ?? '', outcome: 'approve' })}
                              >
                                {t('refunds.approve')}
                              </button>
                              <button
                                type="button"
                                disabled={decide.isPending || !note[refund.id ?? '']}
                                onClick={() => decide.mutate({ id: refund.id ?? '', outcome: 'decline' })}
                              >
                                {t('refunds.decline')}
                              </button>
                            </>
                          ) : (
                            <span className={`pill pill--${(refund.status ?? '').toLowerCase()}`}>
                              {t(`refunds.statuses.${refund.status}`)}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}
      </AsyncSection>
    </>
  );
}
